import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ChevronDown, ChevronRight, Link2, Repeat, RefreshCw, Square } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const pct = (a, b) => (b ? `${((100 * a) / b).toFixed(1)}%` : "—");

/** Article-URL coverage + the NewsAPI.ai backfill (nightly cron, one batch now, or run until done). */
export function SourcesPanel({ onChanged }) {
    const [open, setOpen] = useState(true);
    const [st, setSt] = useState(null);
    const [starting, setStarting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/v1/ripeness/sources/status");
            setSt(r.data);
        } catch (e) { /* admin-only panel */ }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, st?.running ? 5000 : 60000);
        return () => clearInterval(id);
    }, [load, st?.running]);

    useEffect(() => { if (st && !st.running && ["done", "stopped"].includes(st.last_run?.status)) onChanged?.(); }, [st?.running]); // eslint-disable-line react-hooks/exhaustive-deps

    const backfill = useCallback(async (untilDone) => {
        setStarting(true);
        try {
            const r = await api.post("/v1/ripeness/sources/backfill", { reason: "QA dashboard trigger", until_done: untilDone });
            if (r.data.status === "already_running") toast.info("A URL backfill is already running");
            else toast.success(`URL backfill started${untilDone ? " — runs until every record is looked up" : ""} (${r.data.run_id || "queued"})`);
            setTimeout(load, 1500);
        } catch (e) {
            toast.error(`Backfill failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setStarting(false);
        }
    }, [load]);

    const stop = useCallback(async () => {
        try {
            const r = await api.post("/v1/ripeness/sources/backfill/stop");
            toast.info(r.data.status === "stopping" ? "Stopping after the current lookup — progress so far is kept" : "No backfill is running");
            setTimeout(load, 1500);
        } catch (e) {
            toast.error(`Stop failed: ${formatApiErrorDetail(e)}`);
        }
    }, [load]);

    const last = st?.last_run;
    const running = !!st?.running;
    return (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="sources-panel">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 mb-2" data-testid="sources-panel-toggle">
                <span className="inline-flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Article sources
                    {st ? <span className="text-slate-600 normal-case tracking-normal" data-testid="sources-coverage-pct">({pct(st.tracks.with_source_url, st.tracks.total)} of tracks)</span> : null}
                </span>
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            {open && st ? (
                <div className="text-xs space-y-2">
                    <div className="text-slate-500" data-testid="sources-summary">
                        <span className="text-slate-300">{st.tracks.with_source_url.toLocaleString()}</span> / {st.tracks.total.toLocaleString()} tracks with a URL ·{" "}
                        <span className="text-slate-300">{st.records.with_url.toLocaleString()}</span> / {st.records.total.toLocaleString()} records ·{" "}
                        <span className="text-amber-200">{st.records.pending_candidates.toLocaleString()}</span> still to look up · {st.records.no_match.toLocaleString()} not found
                    </div>
                    <div className="text-slate-500" data-testid="sources-schedule">
                        NewsAPI.ai {st.configured ? <span className="text-emerald-300">configured</span> : <span className="text-rose-300">no key</span>}
                        {" · "}nightly <code className="text-slate-400">{st.schedule.cron.expression}</code> UTC · {st.max_records_per_run} records / run
                    </div>
                    {last ? (
                        <div className="border border-slate-800 rounded p-2" data-testid="sources-last-run">
                            <div className="flex items-center gap-2">
                                <span className={`rounded-full border px-2 py-0.5 uppercase tracking-wider ${last.status === "done" ? "border-emerald-400/40 text-emerald-300" : last.status === "failed" ? "border-rose-400/40 text-rose-300" : running ? "border-sky-400/40 text-sky-300" : "border-slate-500/30 text-slate-400"}`} data-testid="sources-last-run-status">
                                    {running ? <RefreshCw className="w-3 h-3 inline mr-1 animate-spin" /> : null}{last.status}
                                </span>
                                <span className="text-slate-400 font-mono truncate">{last.run_id}</span>
                                <span className="ml-auto text-slate-500" data-testid="sources-last-run-mode">{last.mode === "until_done" ? `until done · batch ${last.batches || 0}` : "one batch"} · {last.trigger}</span>
                            </div>
                            <div className="text-slate-500 mt-1" data-testid="sources-last-run-counts">
                                {last.looked_up} looked up · <span className="text-emerald-300">{last.matched} matched</span> · {last.no_match} not found · {last.errors} errors · {last.tracks_reprojected} tracks updated
                            </div>
                            <div className="text-slate-600 mt-0.5">{fmt(last.finished_at || last.started_at)}{last.error ? <span className="text-rose-300"> · {last.error}</span> : null}</div>
                        </div>
                    ) : <div className="text-slate-500 italic" data-testid="sources-no-run">No backfill run yet.</div>}
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => backfill(false)} disabled={starting || running || !st.configured}
                            title={running ? "A backfill is running" : `Look up the next ${st.max_records_per_run} historical records on NewsAPI.ai`}
                            className="inline-flex items-center gap-1 rounded bg-sky-500/80 hover:bg-sky-500 px-2 py-1 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="sources-backfill-btn">
                            <RefreshCw className={`w-3 h-3 ${starting || running ? "animate-spin" : ""}`} /> Backfill next batch
                        </button>
                        <button onClick={() => backfill(true)} disabled={starting || running || !st.configured || !st.records.pending_candidates}
                            title={`Keep looking up batches until all ${st.records.pending_candidates.toLocaleString()} records are done (1 NewsAPI.ai lookup each). Resumes after a restart.`}
                            className="inline-flex items-center gap-1 rounded bg-emerald-600/80 hover:bg-emerald-600 px-2 py-1 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="sources-backfill-all-btn">
                            <Repeat className="w-3 h-3" /> Run until done
                        </button>
                        {running ? (
                            <button onClick={stop} title="Stop after the current lookup; progress so far is kept"
                                className="inline-flex items-center gap-1 rounded border border-rose-400/50 text-rose-200 hover:bg-rose-500/20 px-2 py-1 transition-colors"
                                data-testid="sources-backfill-stop-btn">
                                <Square className="w-3 h-3" /> Stop
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
